import { CampaignStatusBadge } from '../features/campaigns/components/CampaignStatusBadge';
import { CampaignChannelStatuses } from '../features/campaigns/components/CampaignChannelStatuses';
import { CampaignCardMedia } from '../features/campaigns/components/CampaignCardMedia';
import { CampaignOriginDetails } from '../features/campaigns/components/CampaignOriginDetails';
import { campaignPlatformLabel } from '../features/campaigns/campaignOriginDetails';
import { CampaignStatusButton } from '../features/campaigns/components/CampaignStatusButton';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  AlertCircle, 
  Pause, 
  Play, 
  Plus, 
  Send, 
  Trash2, 
  X, 
  Sparkles, 
  MessageSquare, 
  Search, 
  Tag as TagIcon, 
  Check, 
  Kanban, 
  DollarSign, 
  Image as ImageIcon, 
  RotateCw, 
  Layers, 
  FileText, 
  List, 
  LayoutGrid,
  Download,
  Wand2,
  Bot,
  RefreshCw,
  ShieldCheck
} from 'lucide-react';
import { campaignsApi, Campaign } from '../features/campaigns/campaignsApi';
import { CampaignDrawer } from '../features/campaigns/components/CampaignDrawer';
import { MCACampaignStudioModal } from '../features/campaigns/components/MCACampaignStudioModal';
import { ImportMetaCampaignsModal } from '../features/campaigns/components/ImportMetaCampaignsModal';
import { templatesApi, MessageTemplate } from '../features/campaigns/templatesApi';
import { tagsApi, Tag } from '../features/tags/tagsApi';
import { useSessionStore } from '../stores/sessionStore';
import { getAuthHeaders } from '../services/http';

export function CampaignsPage() {
  const tenantId = useSessionStore(state => state.activeTenantId);
  const queryClient = useQueryClient();
  const session = useSessionStore((state) => state.session);
  const token = session?.access_token;

  // UI States
  const [activeTab, setActiveTab] = useState<'campaigns' | 'meta_ads' | 'templates'>('meta_ads');
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>(() => {
    try {
      return (localStorage.getItem('crm_campaigns_view_mode') as 'kanban' | 'list') || 'kanban';
    } catch {
      return 'kanban';
    }
  });

  const handleSetViewMode = (mode: 'kanban' | 'list') => {
    setViewMode(mode);
    try {
      localStorage.setItem('crm_campaigns_view_mode', mode);
    } catch {}
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [hidePaused, setHidePaused] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  
  // Modal States
  const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isIntakeModalOpen, setIsIntakeModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  // Form States - Campaigns
  const [campaignName, setCampaignName] = useState('');
  const [selectedTagId, setSelectedTagId] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  // Form States - Templates
  const [templateName, setTemplateName] = useState('');
  const [templateCategory, setTemplateCategory] = useState<'MARKETING' | 'UTILITY' | 'AUTHENTICATION'>('MARKETING');
  const [templateBody, setTemplateBody] = useState('');
  const [templateLanguage, setTemplateLanguage] = useState('pt_BR');

  // AI Template Generator States
  const [showAiAssistant, setShowAiAssistant] = useState(false);
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const [aiObjective, setAiObjective] = useState('');
  const [aiNiche, setAiNiche] = useState('');
  const [aiTone, setAiTone] = useState('persuasivo e consultivo');
  const [aiExplanation, setAiExplanation] = useState<string | null>(null);

  // Queries
  const campaignsQuery = useQuery({
    queryKey: ['campaigns', token, tenantId],
    queryFn: () => campaignsApi.list({ headers: getAuthHeaders(token!) }),
    enabled: !!token
  });

  const templatesQuery = useQuery({
    queryKey: ['templates', token],
    queryFn: () => templatesApi.list({ headers: getAuthHeaders(token!) }),
    enabled: !!token
  });

  const tagsQuery = useQuery({
    queryKey: ['tags', token],
    queryFn: () => tagsApi.list(token!),
    enabled: !!token
  });

  // Mutations
  const createCampaignMutation = useMutation({
    mutationFn: (data: Partial<Campaign>) => campaignsApi.create(data, { headers: getAuthHeaders(token!) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      setIsCampaignModalOpen(false);
      resetCampaignForm();
    }
  });

  const dispatchCampaignMutation = useMutation({
    mutationFn: (id: string) => {
      return fetch(`/v1/crm/campaigns/${id}/dispatch`, {
        method: 'POST',
        headers: {
          'x-tenant-id': 'vanguarda',
          'Authorization': `Bearer ${token}`
        }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    }
  });

  const createTemplateMutation = useMutation({
    mutationFn: (data: Partial<MessageTemplate>) => templatesApi.create(data, { headers: getAuthHeaders(token!) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      setIsTemplateModalOpen(false);
      resetTemplateForm();
    }
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: (id: string) => templatesApi.delete(id, { headers: getAuthHeaders(token!) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    }
  });

  const fixTypesMutation = useMutation({
    mutationFn: () => campaignsApi.fixTypes({ headers: getAuthHeaders(token!) }),
    onSuccess: (data) => {
      alert(data.message);
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (err: any) => alert('Error migrating: ' + err.message)
  });

  // Helpers
  const resetCampaignForm = () => {
    setCampaignName('');
    setSelectedTagId('');
    setSelectedTemplateId('');
  };

  const resetTemplateForm = () => {
    setTemplateName('');
    setTemplateCategory('MARKETING');
    setTemplateBody('');
    setTemplateLanguage('pt_BR');
    setAiObjective('');
    setAiNiche('');
    setAiExplanation(null);
    setShowAiAssistant(false);
  };

  const handleGenerateAiTemplate = async () => {
    setIsAiGenerating(true);
    setAiExplanation(null);
    try {
      const res = await templatesApi.generateWithAi(
        {
          objective: aiObjective || 'Oferta Especial de Alto Impacto',
          businessNiche: aiNiche || 'Serviços e Soluções Comerciais',
          tone: aiTone,
          category: templateCategory,
        },
        { headers: getAuthHeaders(token!) }
      );

      if (res?.template) {
        setTemplateName(res.template.name);
        setTemplateBody(res.template.body);
        setTemplateCategory(res.template.category || 'MARKETING');
        setAiExplanation(res.template.explanation || 'Template Meta WABA otimizado com IA para máxima taxa de resposta.');
      }
    } catch (err: any) {
      alert('Erro ao gerar modelo com IA: ' + (err.message || err));
    } finally {
      setIsAiGenerating(false);
    }
  };

  const formatTemplateName = (val: string) => {
    return val.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  };

  const handleCreateCampaign = (e: React.FormEvent) => {
    e.preventDefault();
    if (!campaignName || !selectedTemplateId) return;

    const selectedTpl = templates.find(t => t.id === selectedTemplateId);
    
    createCampaignMutation.mutate({
      name: campaignName,
      status: 'draft',
      message_template: selectedTpl?.body || '',
      target_audience: { tag_id: selectedTagId },
    });
  };

  const handleCreateTemplate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!templateName || !templateBody) return;

    createTemplateMutation.mutate({
      name: formatTemplateName(templateName),
      category: templateCategory,
      body: templateBody,
      language: templateLanguage,
      status: 'APPROVED',
    });
  };

  const campaigns = campaignsQuery.data?.items || [];
  const templates = templatesQuery.data?.items || [];
  const tags = tagsQuery.data?.items || [];

  const whatsappCampaigns = campaigns.filter(c => 
    c.campaign_type === 'whatsapp_blast' && (c.name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const metaAdsCampaigns = campaigns.filter(c => {
    if (c.campaign_type === 'whatsapp_blast') return false;
    if (hidePaused && (c.status === 'pausada' || c.status === 'paused')) return false;
    if (!((c.name || '').toLowerCase().includes(searchQuery.toLowerCase()))) return false;
    return true;
  });

  const KANBAN_COLUMNS = [
    { id: 'backlog', label: 'Rascunhos & Backlog', statuses: ['briefing', 'draft', 'rascunho', 'backlog', 'delete_failed', 'deleting'] },
    { id: 'todo', label: 'A Fazer / Em Revisão', statuses: ['planejada', 'em_revisao', 'review', 'validacao'] },
    { id: 'in-progress', label: 'Em Andamento / Ativas', statuses: ['pronta_publicacao', 'publicando', 'ativa', 'active', 'otimizando', 'bloqueada', 'erro', 'running', 'in_progress'] },
    { id: 'paused', label: 'Pausadas & Legado', statuses: ['pausada', 'paused', 'legado'] },
    { id: 'done', label: 'Concluídas / Arquivadas', statuses: ['concluida', 'completed', 'arquivada', 'archived'] }
  ];

  const ALL_KNOWN_STATUSES = KANBAN_COLUMNS.flatMap(col => col.statuses);

  const isCampaignInColumn = (c: Campaign, col: typeof KANBAN_COLUMNS[0]) => {
    const s = (c.status || 'draft').toLowerCase();
    if (col.statuses.includes(s)) return true;
    if (col.id === 'backlog' && !ALL_KNOWN_STATUSES.includes(s)) return true;
    return false;
  };

  const formatCampaignDate = (rawDate: any) => {
    if (!rawDate) return '-';
    try {
      let clean = String(rawDate).trim();
      clean = clean.replace(/^"+|"+$/g, '');
      if (!isNaN(Number(clean)) && !clean.includes('-') && !clean.includes('T')) {
        const num = Number(clean);
        const ms = num < 10000000000 ? num * 1000 : num;
        const d = new Date(ms);
        if (!isNaN(d.getTime())) return d.toLocaleDateString('pt-BR');
      }
      const d = new Date(clean);
      if (!isNaN(d.getTime())) return d.toLocaleDateString('pt-BR');
      return '-';
    } catch {
      return '-';
    }
  };

  const getCampaignBudgetDaily = (camp: any): number | null => {
    if (!camp || camp.budget == null) return null;
    if (typeof camp.budget === 'number') return camp.budget;
    if (typeof camp.budget === 'string') {
      try {
        const parsed = JSON.parse(camp.budget);
        return parsed?.daily ?? null;
      } catch {
        const num = parseFloat(camp.budget);
        return isNaN(num) ? null : num;
      }
    }
    return camp.budget.daily ?? null;
  };

  const filteredTemplates = templates.filter(t => 
    (t.name || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
    (t.body || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="management-page animate-in">
      {/* Hero Header */}
      <section className="hero-card management-hero">
        <div className="management-hero-content">
          <div className="management-hero-copy">
            <p className="eyebrow">Marketing & Aquisição de Clientes</p>
            <h2>Gestão de Campanhas & Anúncios</h2>
            <p className="muted">Criação automatizada de variações de anúncios, públicos segmentados e esteira de publicação direta.</p>
          </div>
          <div className="management-hero-actions" style={{ display: 'flex', gap: '0.75rem' }}>
            {activeTab === 'campaigns' && (
              <button className="primary-button" onClick={() => setIsCampaignModalOpen(true)}>
                <Plus size={18} /> Novo Disparo WhatsApp
              </button>
            )}
            {activeTab === 'meta_ads' && (
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button 
                  className="secondary-button campaign-import-meta-button"
                  onClick={() => setIsImportModalOpen(true)}
                  title="Importar campanhas existentes diretamente da sua conta Meta Ads"
                >
                  <Download size={18} /> Importar da Meta Ads
                </button>
                <button 
                  className="primary-button" 
                  onClick={() => setIsIntakeModalOpen(true)}
                  title="Criar nova campanha inteligente com IA (Matriz Narrativa de 5 Ângulos)"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <Sparkles size={18} /> Nova Campanha de Anúncios
                </button>
              </div>
            )}
            {activeTab === 'templates' && (
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button 
                  className="secondary-button" 
                  onClick={() => {
                    resetTemplateForm();
                    setShowAiAssistant(true);
                    setIsTemplateModalOpen(true);
                  }}
                  title="Criar modelo oficial WABA otimizado com IA"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <Sparkles size={18} style={{ color: '#a78bfa' }} /> Gerar Modelo com IA
                </button>
                <button 
                  className="primary-button" 
                  onClick={() => {
                    resetTemplateForm();
                    setIsTemplateModalOpen(true);
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <Plus size={18} /> Novo Modelo
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Modern Tabs Bar */}
      <div className="tabs-container" style={{ display: 'flex', gap: '1rem', borderBottom: '1px solid var(--border-color)', marginBottom: '2rem', paddingBottom: '0.5rem' }}>
        <button 
          onClick={() => { setActiveTab('meta_ads'); setSearchQuery(''); }}
          style={{
            background: 'none',
            border: 'none',
            color: activeTab === 'meta_ads' ? 'var(--primary-color)' : 'var(--text-muted)',
            fontWeight: 600,
            fontSize: '1rem',
            padding: '0.5rem 1rem',
            cursor: 'pointer',
            borderBottom: activeTab === 'meta_ads' ? '2px solid var(--primary-color)' : '2px solid transparent',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s'
          }}
        >
          <Layers size={18} /> Esteira de Tráfego ({metaAdsCampaigns.length})
        </button>
        <button 
          onClick={() => { setActiveTab('campaigns'); setSearchQuery(''); }}
          style={{
            background: 'none',
            border: 'none',
            color: activeTab === 'campaigns' ? 'var(--primary-color)' : 'var(--text-muted)',
            fontWeight: 600,
            fontSize: '1rem',
            padding: '0.5rem 1rem',
            cursor: 'pointer',
            borderBottom: activeTab === 'campaigns' ? '2px solid var(--primary-color)' : '2px solid transparent',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s'
          }}
        >
          <Send size={18} /> Disparos em Massa ({whatsappCampaigns.length})
        </button>
        <button 
          onClick={() => { setActiveTab('templates'); setSearchQuery(''); }}
          style={{
            background: 'none',
            border: 'none',
            color: activeTab === 'templates' ? 'var(--primary-color)' : 'var(--text-muted)',
            fontWeight: 600,
            fontSize: '1rem',
            padding: '0.5rem 1rem',
            cursor: 'pointer',
            borderBottom: activeTab === 'templates' ? '2px solid var(--primary-color)' : '2px solid transparent',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s'
          }}
        >
          <FileText size={18} /> Modelos de Mensagem ({templates.length})
        </button>
      </div>

      {/* Search & Global Actions */}
      <div className="management-toolbar" style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginBottom: '2rem' }}>
        <div className="search-box" style={{ position: 'relative', width: '100%', maxWidth: '400px' }}>
          <Search size={18} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input 
            type="text" 
            placeholder={`Buscar por nome da campanha...`} 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
            style={{ width: '100%', padding: '0.75rem 1rem 0.75rem 2.75rem', background: 'var(--bg-glass)', border: '1px solid var(--border-color)', borderRadius: '12px', color: 'var(--text-color)' }}
          />
        </div>
      </div>

      {/* Tab Contents */}
      {activeTab === 'campaigns' ? (
        whatsappCampaigns.length === 0 ? (
          <div className="empty-state">
            <Send size={48} className="muted" />
            <p>Nenhum disparo encontrado.</p>
          </div>
        ) : (
          <div className="management-list" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
            {whatsappCampaigns.map((campaign: Campaign) => (
              <article key={campaign.id} className="page-card management-panel" onClick={() => setSelectedCampaign(campaign)} style={{ background: 'var(--bg-glass)', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '1.5rem', cursor: 'pointer' }}>
                <div className="management-card-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <div className="management-card-copy">
                    <div className="management-inline-cluster" style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <strong style={{ fontSize: '1.2rem', color: 'var(--text-color)' }}>{campaign.name}</strong>
                      <CampaignStatusBadge campaign={campaign} />
                    </div>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', fontStyle: 'italic' }}>
                      "{campaign.message_template || 'Sem template definido.'}"
                    </p>
                  </div>
                  <div className="management-actions">
                    {campaign.status === 'draft' && (
                      <button 
                        onClick={() => dispatchCampaignMutation.mutate(campaign.id)}
                        className="primary-button" 
                        style={{ padding: '0.5rem 1rem', display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.85rem' }}
                      >
                        <Play size={14} /> Disparar Agora
                      </button>
                    )}
                  </div>
                </div>

                <div className="management-card-grid two-cols" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1.5rem' }}>
                  <article className="management-metric-card" style={{ background: 'rgba(255,255,255,0.02)', padding: '1rem', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <div className="management-metric-copy">
                      <strong style={{ fontSize: '1.5rem', display: 'block' }}>
                        {campaign.status === 'completed' ? '100%' : campaign.status === 'running' ? 'Em andamento' : 'Pendente'}
                      </strong>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Status de Envio</span>
                    </div>
                  </article>
                  <article className="management-metric-card" style={{ background: 'rgba(255,255,255,0.02)', padding: '1rem', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <div className="management-metric-copy">
                      <strong style={{ fontSize: '1.5rem', display: 'block' }}>
                        {campaign.target_audience?.tag_id ? 'Público com Tag' : 'Todos os Contatos'}
                      </strong>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Filtro de Destinatários</span>
                    </div>
                  </article>
                </div>
              </article>
            ))}
          </div>
        )
      ) : activeTab === 'meta_ads' ? (
        /* META ADS TAB (KANBAN OR TABLE LIST) */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{ display: 'flex', background: 'rgba(255,255,255,0.05)', padding: '3px', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
                <button
                  onClick={() => handleSetViewMode('kanban')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    padding: '0.4rem 0.8rem',
                    borderRadius: '8px',
                    border: 'none',
                    background: viewMode === 'kanban' ? 'var(--primary-color)' : 'transparent',
                    color: viewMode === 'kanban' ? '#fff' : 'var(--text-muted)',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <LayoutGrid size={15} /> Visualização em Esteira (Kanban)
                </button>
                <button
                  onClick={() => handleSetViewMode('list')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    padding: '0.4rem 0.8rem',
                    borderRadius: '8px',
                    border: 'none',
                    background: viewMode === 'list' ? 'var(--primary-color)' : 'transparent',
                    color: viewMode === 'list' ? '#fff' : 'var(--text-muted)',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <List size={15} /> Todas as Campanhas (Tabela / Legado)
                </button>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: 'var(--text-color)', fontSize: '0.85rem' }}>
                <input type="checkbox" checked={hidePaused} onChange={(e) => setHidePaused(e.target.checked)} style={{ accentColor: 'var(--primary-color)' }} />
                Ocultar Pausadas
              </label>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <button 
                className="secondary-button" 
                onClick={() => setIsImportModalOpen(true)}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.4rem', 
                  fontSize: '0.85rem', 
                  padding: '0.4rem 0.8rem',
                  borderRadius: '8px'
                }}
                title="Sincronizar e importar campanhas antigas da Meta Ads"
              >
                <Download size={14} /> Importar da Meta
              </button>
              <span style={{ 
                fontSize: '0.85rem', 
                color: 'var(--text-muted)',
                background: 'rgba(255,255,255,0.04)',
                padding: '0.35rem 0.75rem',
                borderRadius: '8px',
                border: '1px solid var(--border-color)'
              }}>
                Total: <strong style={{ color: 'var(--text-color)' }}>{metaAdsCampaigns.length}</strong> campanhas
              </span>
            </div>
          </div>

          {viewMode === 'kanban' ? (
            /* KANBAN BOARD */
            <div className="kanban-board" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(260px, 1fr))', gap: '1rem', overflowX: 'auto', paddingBottom: '1rem' }}>
              {KANBAN_COLUMNS.map((column) => (
                <div key={column.id} className="kanban-column" style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '16px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem', border: '1px solid var(--border-color)', minHeight: '60vh' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong style={{ color: 'var(--text-muted)', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{column.label}</strong>
                    <span style={{ background: 'rgba(255,255,255,0.1)', padding: '0.15rem 0.5rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600 }}>
                      {metaAdsCampaigns.filter((c: Campaign) => isCampaignInColumn(c, column)).length}
                    </span>
                  </div>
                  
                  <div className="kanban-column-cards" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {metaAdsCampaigns
                      .filter((c: Campaign) => isCampaignInColumn(c, column))
                      .map((campaign: Campaign) => {
                        const objectiveMap: Record<string, string> = {
                          lead_generation: 'Geração de Leads',
                          sales: 'Vendas',
                          awareness: 'Reconhecimento',
                          engagement: 'Engajamento'
                        };
                        
                        const syncColor = campaign.meta_sync_status === 'synced' ? '#10b981' : 
                                          campaign.meta_sync_status === 'error' ? '#ef4444' : 
                                          campaign.meta_sync_status === 'pending' ? '#f59e0b' : 'transparent';
                        

                        return (
                          <article 
                            key={campaign.id} 
                            className="kanban-card page-card animate-in" 
                            onClick={() => setSelectedCampaign(campaign)}
                            style={{ padding: '1rem', background: 'var(--bg-glass)', borderRadius: '12px', border: '1px solid var(--border-color)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', gap: '0.75rem', cursor: 'pointer', position: 'relative' }}
                          >
                            {syncColor !== 'transparent' && (
                              <div title={`Meta Sync: ${campaign.meta_sync_status}`} style={{ position: 'absolute', top: '-4px', right: '-4px', width: '12px', height: '12px', borderRadius: '50%', background: syncColor, border: '2px solid #18181b' }} />
                            )}

                            <CampaignCardMedia campaign={campaign} />

                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <strong style={{ fontSize: '0.95rem', color: 'var(--text-color)', lineHeight: 1.3 }}>{campaign.name}</strong>
                          </div>

                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <CampaignStatusBadge campaign={campaign} />
                            {campaign.meta_adset_id && (
                              <span style={{ fontSize: '0.75rem', color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '0.15rem 0.4rem', borderRadius: '4px', border: '1px solid rgba(16,185,129,0.2)' }}>
                                Meta ID: {campaign.meta_adset_id}
                              </span>
                            )}
                          </div>

                          <CampaignStatusButton campaign={campaign} />
                          <CampaignOriginDetails campaign={campaign} />

                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                              <Sparkles size={12} /> {objectiveMap[campaign.objective || ''] || campaign.objective || campaignPlatformLabel(campaign)}
                            </span>
                            {getCampaignBudgetDaily(campaign) != null && (
                              <span style={{ fontWeight: 600, color: 'var(--primary-color)', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                                <DollarSign size={12} /> {getCampaignBudgetDaily(campaign)}/dia
                              </span>
                            )}
                          </div>
                        </article>
                      );
                    })}
                    {metaAdsCampaigns.filter((c: Campaign) => isCampaignInColumn(c, column)).length === 0 && (
                      <div style={{ padding: '1rem', border: '1px dashed var(--border-color)', borderRadius: '12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                        Nenhuma campanha
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* TABELA COMPLETA COM TODAS AS CAMPANHAS (LEGADO + NOVAS) */
            <div className="page-card" style={{ background: 'var(--bg-glass)', border: '1px solid var(--border-color)', borderRadius: '16px', overflow: 'hidden', padding: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid var(--border-color)', color: 'var(--text-muted)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    <th style={{ padding: '1rem 1.25rem' }}>Campanha / Anúncio</th>
                    <th style={{ padding: '1rem 1.25rem' }}>Status</th>
                    <th style={{ padding: '1rem 1.25rem' }}>Objetivo</th>
                    <th style={{ padding: '1rem 1.25rem' }}>Orçamento</th>
                    <th style={{ padding: '1rem 1.25rem' }}>Criado em</th>
                    <th style={{ padding: '1rem 1.25rem', textAlign: 'right' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {metaAdsCampaigns.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                        Nenhuma campanha encontrada com os filtros selecionados.
                      </td>
                    </tr>
                  ) : (
                    metaAdsCampaigns.map((camp) => (
                      <tr 
                        key={camp.id} 
                        onClick={() => setSelectedCampaign(camp)}
                        style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer', transition: 'background 0.2s' }}
                        className="hover:bg-white/5"
                      >
                        <td style={{ padding: '1rem 1.25rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            {camp.primary_asset_url || camp.creatives?.[0]?.asset_url ? (
                              <img 
                                src={(camp.primary_asset_url || camp.creatives?.[0]?.asset_url) ?? undefined} 
                                alt="" 
                                style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover', background: '#000' }} 
                              />
                            ) : (
                              <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <ImageIcon size={18} style={{ color: 'var(--text-muted)' }} />
                              </div>
                            )}
                            <div>
                              <strong style={{ color: 'var(--text-color)', display: 'block', fontSize: '0.95rem' }}>{camp.name}</strong>
                              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>ID: {camp.id}</span>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '1rem 1.25rem' }}>
                          <CampaignStatusBadge campaign={camp} />
                          <CampaignChannelStatuses campaign={camp} />
                        </td>
                        <td style={{ padding: '1rem 1.25rem', color: 'var(--text-muted)' }}>
                          {camp.objective || campaignPlatformLabel(camp)}
                        </td>
                        <td style={{ padding: '1rem 1.25rem', fontWeight: 600, color: 'var(--primary-color)' }}>
                          {getCampaignBudgetDaily(camp) != null ? `R$ ${getCampaignBudgetDaily(camp)}/dia` : '-'}
                        </td>
                        <td style={{ padding: '1rem 1.25rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                          {formatCampaignDate(camp.created_at)}
                        </td>
                        <td style={{ padding: '1rem 1.25rem', textAlign: 'right' }}>
                          <CampaignStatusButton campaign={camp} />
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCampaign(camp);
                            }}
                            className="secondary-button"
                            style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}
                          >
                            Abrir Painel
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        /* TEMPLATES TAB */
        filteredTemplates.length === 0 ? (
          <div className="empty-state">
            <MessageSquare size={48} className="muted" />
            <p>Nenhum modelo de mensagem criado.</p>
          </div>
        ) : (
          <div className="management-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
            {filteredTemplates.map((template: MessageTemplate) => (
              <article 
                key={template.id} 
                className="page-card management-panel animate-in" 
                style={{ 
                  background: 'var(--bg-glass)', 
                  border: '1px solid var(--border-color)', 
                  borderRadius: '16px', 
                  padding: '1.5rem', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  justifyContent: 'space-between',
                  minHeight: '220px',
                  boxShadow: '0 8px 32px 0 rgba(0,0,0,0.2)',
                  backdropFilter: 'blur(8px)'
                }}
              >
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--primary-color)', letterSpacing: '0.05em' }}>
                          {template.category}
                        </span>
                        {template.meta_template_id && (
                          <span style={{
                            fontSize: '0.65rem',
                            padding: '0.1rem 0.45rem',
                            borderRadius: '4px',
                            background: 'rgba(59, 130, 246, 0.12)',
                            color: '#60a5fa',
                            border: '1px solid rgba(59, 130, 246, 0.25)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}>
                            <ShieldCheck size={10} /> Meta WABA
                          </span>
                        )}
                      </div>
                      <strong style={{ fontSize: '1.15rem', color: 'var(--text-color)', wordBreak: 'break-word' }}>
                        {template.name}
                      </strong>
                    </div>
                    <span 
                      style={{ 
                        fontSize: '0.75rem', 
                        padding: '0.25rem 0.6rem', 
                        borderRadius: '999px', 
                        background: template.status === 'REJECTED' 
                          ? 'rgba(239, 68, 68, 0.1)' 
                          : template.status === 'PENDING' 
                            ? 'rgba(245, 158, 11, 0.1)' 
                            : 'rgba(16, 185, 129, 0.1)', 
                        color: template.status === 'REJECTED' 
                          ? '#ef4444' 
                          : template.status === 'PENDING' 
                            ? '#fbbf24' 
                            : '#10b981', 
                        fontWeight: 600,
                        border: template.status === 'REJECTED' 
                          ? '1px solid rgba(239, 68, 68, 0.2)' 
                          : template.status === 'PENDING' 
                            ? '1px solid rgba(245, 158, 11, 0.2)' 
                            : '1px solid rgba(16, 185, 129, 0.2)',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {template.status === 'REJECTED' ? 'Rejeitado' : template.status === 'PENDING' ? 'Em Análise' : 'Aprovado'}
                    </span>
                  </div>
                  
                  <div 
                    style={{ 
                      background: 'rgba(0,0,0,0.15)', 
                      padding: '0.75rem 1rem', 
                      borderRadius: '12px', 
                      border: '1px solid rgba(255,255,255,0.05)',
                      fontSize: '0.9rem',
                      lineHeight: '1.4',
                      color: 'rgba(255,255,255,0.85)',
                      whiteSpace: 'pre-wrap',
                      marginTop: '0.75rem',
                      marginBottom: '1rem',
                      fontFamily: 'monospace'
                    }}
                  >
                    {template.body}
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '0.75rem' }}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Idioma: {template.language}</span>
                  <button 
                    onClick={() => {
                      if (confirm('Tem certeza que deseja excluir este modelo da sua lista?')) {
                        deleteTemplateMutation.mutate(template.id);
                      }
                    }}
                    style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0.25rem', borderRadius: '6px' }}
                    className="delete-button-hover"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )
      )}

      {/* NEW CAMPAIGN (WHATSAPP) MODAL */}
      {isCampaignModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div className="page-card animate-in" style={{ width: '90%', maxWidth: '500px', background: '#121214', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text-color)', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <Sparkles size={20} style={{ color: 'var(--primary-color)' }} /> Nova Campanha WhatsApp
              </h3>
              <button onClick={() => setIsCampaignModalOpen(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleCreateCampaign} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>Nome da Campanha</label>
                <input 
                  type="text" 
                  required
                  placeholder="Ex: Campanha de Vendas de Maio"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border-color)', background: '#18181b', color: '#fff' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>Público Alvo (Tag)</label>
                <select 
                  value={selectedTagId}
                  onChange={(e) => setSelectedTagId(e.target.value)}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border-color)', background: '#18181b', color: '#fff' }}
                >
                  <option value="">Enviar para Todos (Sem filtro de Tag)</option>
                  {tags.map((tag: Tag) => (
                    <option key={tag.id} value={tag.id}>{tag.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>Modelo de Mensagem (WhatsApp Template)</label>
                <select 
                  required
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border-color)', background: '#18181b', color: '#fff' }}
                >
                  <option value="">Selecione um modelo aprovado...</option>
                  {templates.map((tpl: MessageTemplate) => (
                    <option key={tpl.id} value={tpl.id}>{tpl.name} ({tpl.category})</option>
                  ))}
                </select>
                {templates.length === 0 && (
                  <span style={{ fontSize: '0.8rem', color: '#ef4444', marginTop: '0.25rem', display: 'block' }}>
                    ⚠️ Você precisa criar um modelo de mensagem primeiro na aba correspondente!
                  </span>
                )}
              </div>

              {selectedTemplateId && (
                <div style={{ background: 'rgba(255,255,255,0.02)', padding: '1rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>Visualização do Modelo Selecionado:</span>
                  <p style={{ fontStyle: 'italic', fontSize: '0.85rem', color: '#fff', whiteSpace: 'pre-wrap' }}>
                    "{templates.find(t => t.id === selectedTemplateId)?.body}"
                  </p>
                </div>
              )}

              <button 
                type="submit" 
                className="primary-button" 
                style={{ width: '100%', padding: '0.85rem', marginTop: '1rem', fontSize: '1rem' }}
                disabled={createCampaignMutation.isPending}
              >
                {createCampaignMutation.isPending ? 'Criando...' : 'Criar Campanha'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* NEW TEMPLATE MODAL */}
      {isTemplateModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div className="page-card animate-in" style={{ width: '95%', maxWidth: '600px', background: '#121214', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text-color)', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <MessageSquare size={20} style={{ color: 'var(--primary-color)' }} /> Novo Modelo de Mensagem Oficial
              </h3>
              <button onClick={() => setIsTemplateModalOpen(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleCreateTemplate} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              {/* AI Generation Assistant Section */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.12) 0%, rgba(59, 130, 246, 0.08) 100%)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '14px',
                padding: '1.25rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showAiAssistant ? '1rem' : 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <div style={{
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)',
                      borderRadius: '8px',
                      padding: '0.35rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff'
                    }}>
                      <Sparkles size={18} />
                    </div>
                    <div>
                      <strong style={{ fontSize: '0.95rem', color: '#f4f4f5', display: 'block' }}>
                        Assistente de Copy IA (Meta WABA Compliance)
                      </strong>
                      <span style={{ fontSize: '0.75rem', color: '#a1a1aa' }}>
                        Gere automaticamente templates persuasivos já formatados com parâmetros variáveis {"{{1}}"}, {"{{2}}"}
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowAiAssistant(!showAiAssistant)}
                    style={{
                      background: showAiAssistant ? 'rgba(139, 92, 246, 0.25)' : 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(139, 92, 246, 0.4)',
                      color: '#c4b5fd',
                      padding: '0.35rem 0.75rem',
                      borderRadius: '8px',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.35rem'
                    }}
                  >
                    <Wand2 size={14} />
                    {showAiAssistant ? 'Ocultar Assistente' : 'Usar Assistente'}
                  </button>
                </div>

                {showAiAssistant && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', marginTop: '0.5rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '0.75rem' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#d4d4d8', marginBottom: '0.3rem' }}>
                          Objetivo da Mensagem
                        </label>
                        <input 
                          type="text" 
                          placeholder="Ex: Oferta exclusiva, Confirmação, Reativação..."
                          value={aiObjective}
                          onChange={(e) => setAiObjective(e.target.value)}
                          style={{ width: '100%', padding: '0.55rem', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.15)', background: 'rgba(0, 0, 0, 0.3)', color: '#fff', fontSize: '0.85rem' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#d4d4d8', marginBottom: '0.3rem' }}>
                          Nicho / Segmento
                        </label>
                        <input 
                          type="text" 
                          placeholder="Ex: Consultoria B2B, Clínica, Imóveis..."
                          value={aiNiche}
                          onChange={(e) => setAiNiche(e.target.value)}
                          style={{ width: '100%', padding: '0.55rem', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.15)', background: 'rgba(0, 0, 0, 0.3)', color: '#fff', fontSize: '0.85rem' }}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '180px' }}>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#d4d4d8', marginBottom: '0.3rem' }}>
                          Tom de Voz
                        </label>
                        <select
                          value={aiTone}
                          onChange={(e) => setAiTone(e.target.value)}
                          style={{ width: '100%', padding: '0.55rem', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.15)', background: 'rgba(0, 0, 0, 0.3)', color: '#fff', fontSize: '0.85rem' }}
                        >
                          <option value="persuasivo e consultivo">Persuasivo & Comercial (Alto Impacto)</option>
                          <option value="direto e urgente">Direto & Urgente (Gatilhos de Escassez)</option>
                          <option value="formal e executivo">Formal & Executivo (B2B Corporativo)</option>
                          <option value="amigavel e acolhedor">Amigável & Acolhedor (Relacionamento)</option>
                        </select>
                      </div>

                      <button
                        type="button"
                        onClick={handleGenerateAiTemplate}
                        disabled={isAiGenerating}
                        className="primary-button"
                        style={{
                          padding: '0.55rem 1.25rem',
                          fontSize: '0.85rem',
                          background: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.4rem',
                          height: '38px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {isAiGenerating ? (
                          <>
                            <RefreshCw size={15} className="animate-spin" /> Gerando com IA...
                          </>
                        ) : (
                          <>
                            <Sparkles size={15} /> Gerar Template com IA
                          </>
                        )}
                      </button>
                    </div>

                    {aiExplanation && (
                      <div style={{
                        background: 'rgba(139, 92, 246, 0.15)',
                        border: '1px solid rgba(139, 92, 246, 0.25)',
                        borderRadius: '8px',
                        padding: '0.6rem 0.85rem',
                        fontSize: '0.8rem',
                        color: '#ddd6fe',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <Bot size={16} style={{ flexShrink: 0, color: '#a78bfa' }} />
                        <span>{aiExplanation}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>Nome do Modelo</label>
                  <input 
                    type="text" 
                    required
                    placeholder="Ex: lembrete_consulta"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border-color)', background: '#18181b', color: '#fff' }}
                  />
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem', display: 'block' }}>
                    Visual na Meta: {formatTemplateName(templateName || 'exemplo_nome')}
                  </span>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>Categoria</label>
                  <select 
                    value={templateCategory}
                    onChange={(e) => setTemplateCategory(e.target.value as any)}
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border-color)', background: '#18181b', color: '#fff' }}
                  >
                    <option value="MARKETING">Marketing / Propaganda</option>
                    <option value="UTILITY">Utilitário / Informativo</option>
                    <option value="AUTHENTICATION">Autenticação (OTP)</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>Corpo da Mensagem (Texto)</label>
                <textarea 
                  required
                  rows={4}
                  placeholder="Olá {{1}}, obrigado pelo contato! Seu pedido {{2}} foi confirmado com sucesso."
                  value={templateBody}
                  onChange={(e) => setTemplateBody(e.target.value)}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border-color)', background: '#18181b', color: '#fff', resize: 'vertical' }}
                />
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem', display: 'block' }}>
                  Use as chaves <code>{"{{1}}"}</code>, <code>{"{{2}}"}</code>, etc. para representar valores dinâmicos que o CRM preencherá no disparo da campanha.
                </span>
              </div>

              {/* Dynamic live preview card inside modal */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>Visualização em Tempo Real (WhatsApp Preview)</label>
                <div 
                  style={{ 
                    background: '#075e54', 
                    backgroundImage: 'radial-gradient(#128c7e 10%, transparent 11%)', 
                    backgroundSize: '12px 12px',
                    padding: '1.25rem', 
                    borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start'
                  }}
                >
                  <div 
                    style={{ 
                      background: '#dcf8c6', 
                      color: '#303030', 
                      padding: '0.75rem 1rem', 
                      borderRadius: '0 8px 8px 8px',
                      maxWidth: '85%',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                      fontSize: '0.9rem',
                      lineHeight: '1.4',
                      wordBreak: 'break-word',
                      whiteSpace: 'pre-wrap',
                      position: 'relative'
                    }}
                  >
                    {templateBody || 'Digite o texto acima para visualizar...'}
                    <div style={{ textAlign: 'right', fontSize: '0.7rem', color: '#888', marginTop: '0.25rem' }}>10:00</div>
                  </div>
                </div>
              </div>

              <button 
                type="submit" 
                className="primary-button" 
                style={{ width: '100%', padding: '0.85rem', marginTop: '1rem', fontSize: '1rem' }}
                disabled={createTemplateMutation.isPending}
              >
                {createTemplateMutation.isPending ? 'Cadastrando na Meta...' : 'Salvar Modelo'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* CAMPAIGN DETAILS DRAWER */}
      {selectedCampaign && (
        <CampaignDrawer 
          campaign={campaigns.find(item => item.id === selectedCampaign.id) || selectedCampaign}
          onClose={() => setSelectedCampaign(null)} 
        />
      )}

      {/* MCA ADS CAMPAIGN STUDIO MODAL */}
      <MCACampaignStudioModal
        isOpen={isIntakeModalOpen}
        onClose={() => setIsIntakeModalOpen(false)}
        onSuccess={(campaignId) => {
          queryClient.invalidateQueries({ queryKey: ['campaigns'] });
          setIsIntakeModalOpen(false);
          const newCamp = campaigns.find(c => c.id === campaignId);
          if (newCamp) {
            setSelectedCampaign(newCamp);
          }
        }}
      />

      {/* IMPORT META CAMPAIGNS MODAL */}
      <ImportMetaCampaignsModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['campaigns'] });
        }}
      />
    </div>
  );
}

export default CampaignsPage;
